import React from "react";
import {
  AbsoluteFill,
  useCurrentFrame,
  useVideoConfig,
  spring,
  interpolate,
  Sequence,
  staticFile,
  Img,
} from "remotion";
import {SAFE_ZONES} from "../presets/editing-styles";
import {CLAMP, FONT_STACK, IDV} from "../presets/idv";
import {Noise} from "../components/dimus/Noise";
import {Vignette} from "../components/dimus/Vignette";
import {FilmGrain} from "../components/dimus/FilmGrain";
import {AccentLine} from "../components/dimus/AccentLine";

// ─── Types ────────────────────────────────────────────────────────────

export type TextReelStyle = "cafe" | "verdades" | "aprendi" | "audience";

export interface TextLine {
  text: string;
  isHook?: boolean;
  isEmphasis?: boolean;
  isPause?: boolean;
}

export interface TextReelProps {
  lines: TextLine[];
  style: TextReelStyle;
  badge: string;
  handle?: string;
}

// ─── Style Configs ────────────────────────────────────────────────────

interface StyleConfig {
  bg: string;
  bgGlow?: string;
  textColor: string;
  accentColor: string;
  badgeBg: string;
  badgeText: string;
  fontSizeHook: number;
  fontSizeBody: number;
  fontSizeEmphasis: number;
  fontWeight: number;
  animMode: "slam" | "slide-up" | "slide-left" | "scale-in";
  showGrain: boolean;
  showNoise: boolean;
  showVignette: boolean;
  logoAsset: string;
}

const STYLE_CONFIGS: Record<TextReelStyle, StyleConfig> = {
  cafe: {
    bg: "#0a0805",
    bgGlow: "#3d2a0a",
    textColor: "#f5f0e8",
    accentColor: IDV.third,
    badgeBg: IDV.third,
    badgeText: "#0a0a0a",
    fontSizeHook: 80,
    fontSizeBody: 62,
    fontSizeEmphasis: 68,
    fontWeight: 800,
    animMode: "slide-up",
    showGrain: true,
    showNoise: true,
    showVignette: true,
    logoAsset: "assets/profiles/logo-cafe-roi-dark-bg.png",
  },
  verdades: {
    bg: "#050505",
    bgGlow: "#1a0a03",
    textColor: "#ffffff",
    accentColor: "#fb923c",
    badgeBg: "#fb923c",
    badgeText: "#ffffff",
    fontSizeHook: 96,
    fontSizeBody: 72,
    fontSizeEmphasis: 82,
    fontWeight: 900,
    animMode: "slam",
    showGrain: false,
    showNoise: false,
    showVignette: true,
    logoAsset: "assets/profiles/guilhermeribeiro-ai.jpg",
  },
  aprendi: {
    bg: "#05070f",
    bgGlow: "#0a1030",
    textColor: "#e8ecf8",
    accentColor: "#7c9ef8",
    badgeBg: "#1e2d6e",
    badgeText: "#7c9ef8",
    fontSizeHook: 76,
    fontSizeBody: 60,
    fontSizeEmphasis: 66,
    fontWeight: 700,
    animMode: "slide-left",
    showGrain: true,
    showNoise: true,
    showVignette: true,
    logoAsset: "assets/profiles/guilhermeribeiro-ai.jpg",
  },
  audience: {
    bg: "#000000",
    bgGlow: undefined,
    textColor: "#ffffff",
    accentColor: IDV.secondary,
    badgeBg: IDV.secondary,
    badgeText: "#000000",
    fontSizeHook: 104,
    fontSizeBody: 76,
    fontSizeEmphasis: 88,
    fontWeight: 900,
    animMode: "scale-in",
    showGrain: false,
    showNoise: false,
    showVignette: false,
    logoAsset: "assets/profiles/guilhermeribeiro-ai.jpg",
  },
};

// ─── Timing — aumentado +25% para melhor leitura ──────────────────────

const SCENE_TRANSITION = 15;

const framesForLine = (line: TextLine): number => {
  if (line.isPause) return 30;
  const words = line.text.split(/\s+/).length;
  if (words <= 4)  return 90;   // 3.0s
  if (words <= 8)  return 108;  // 3.6s
  if (words <= 14) return 135;  // 4.5s
  return 165;                   // 5.5s
};

export const calcTextReelTotal = (lines: TextLine[]): number =>
  lines.reduce((s, l) => s + framesForLine(l), 0) +
  lines.length * SCENE_TRANSITION +
  24;

// ─── LineScene ────────────────────────────────────────────────────────

const LineScene: React.FC<{
  line: TextLine;
  duration: number;
  config: StyleConfig;
}> = ({line, duration, config}) => {
  const frame = useCurrentFrame();
  const {fps} = useVideoConfig();

  const fontSize = line.isHook
    ? config.fontSizeHook
    : line.isEmphasis
    ? config.fontSizeEmphasis
    : config.fontSizeBody;

  const textColor = line.isEmphasis ? config.accentColor : config.textColor;

  const springConfig = (() => {
    switch (config.animMode) {
      case "slam":       return {damping: 12, stiffness: 320, mass: 0.5};
      case "scale-in":   return {damping: 18, stiffness: 220, mass: 0.8};
      case "slide-left": return {damping: 22, stiffness: 180, mass: 0.7};
      default:           return {damping: 20, stiffness: 160, mass: 0.7};
    }
  })();

  const p = spring({frame, fps, config: springConfig});
  const fadeIn = interpolate(frame, [0, 10], [0, 1], CLAMP);
  const fadeOut = interpolate(frame, [duration - 18, duration], [1, 0], CLAMP);
  const opacity = Math.min(fadeIn, fadeOut);

  const transform = (() => {
    switch (config.animMode) {
      case "slam":
        return `scale(${interpolate(p, [0, 1], [1.18, 1], CLAMP)})`;
      case "slide-up":
        return `translateY(${interpolate(p, [0, 1], [48, 0], CLAMP)}px)`;
      case "slide-left":
        return `translateX(${interpolate(p, [0, 1], [-70, 0], CLAMP)}px)`;
      case "scale-in":
        return `scale(${interpolate(p, [0, 1], [0.82, 1], CLAMP)}) translateY(${interpolate(p, [0, 1], [24, 0], CLAMP)}px)`;
    }
  })();

  const SZ = SAFE_ZONES.instagram;

  return (
    <AbsoluteFill
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        paddingTop: SZ.top,
        paddingBottom: SZ.bottom,
        paddingLeft: SZ.left,
        paddingRight: SZ.right,
        opacity,
      }}
    >
      <div style={{transform, display: "flex", flexDirection: "column", alignItems: "center", gap: 14}}>
        <div
          style={{
            fontFamily: FONT_STACK,
            fontSize,
            fontWeight: config.fontWeight,
            color: textColor,
            textAlign: "center",
            lineHeight: 1.14,
            letterSpacing: line.isHook ? "-0.025em" : "-0.015em",
            maxWidth: 940,
            textShadow: line.isEmphasis ? `0 0 50px ${textColor}70` : undefined,
          }}
        >
          {line.text}
        </div>
        {line.isEmphasis && (
          <AccentLine color={config.accentColor} delay={10} width={72} />
        )}
      </div>
    </AbsoluteFill>
  );
};

// ─── Background ───────────────────────────────────────────────────────

const TextReelBg: React.FC<{config: StyleConfig}> = ({config}) => {
  const frame = useCurrentFrame();
  const t = frame * 0.003;
  const gx = 50 + Math.sin(t) * 12;
  const gy = 50 + Math.cos(t * 0.7) * 12;

  return (
    <AbsoluteFill>
      <div style={{position: "absolute", inset: 0, background: config.bg}} />
      {config.bgGlow && (
        <div
          style={{
            position: "absolute",
            inset: 0,
            background: `radial-gradient(ellipse 80% 60% at ${gx}% ${gy}%, ${config.bgGlow}90 0%, transparent 70%)`,
          }}
        />
      )}
      {config.showNoise && <Noise opacity={0.04} />}
      {config.showVignette && <Vignette intensity={0.65} />}
      {config.showGrain && <FilmGrain opacity={0.025} />}
    </AbsoluteFill>
  );
};

// ─── Logo + Badge Overlay ─────────────────────────────────────────────

const BadgeOverlay: React.FC<{
  badge: string;
  handle: string;
  config: StyleConfig;
}> = ({badge, handle, config}) => {
  const frame = useCurrentFrame();
  const p = spring({frame, fps: 30, config: {damping: 20, stiffness: 180}});
  const opacity = interpolate(p, [0, 1], [0, 1], CLAMP);
  const SZ = SAFE_ZONES.instagram;

  const logoPulse = spring({
    frame: frame - 8,
    fps: 30,
    config: {damping: 14, stiffness: 200},
  });
  const logoScale = interpolate(logoPulse, [0, 1], [0.6, 1], CLAMP);

  return (
    <AbsoluteFill style={{pointerEvents: "none"}}>
      {/* Logo + Badge — top left */}
      <div
        style={{
          position: "absolute",
          top: SZ.top,
          left: SZ.left,
          opacity,
          display: "flex",
          alignItems: "center",
          gap: 12,
          transform: `translateY(${interpolate(p, [0, 1], [-12, 0], CLAMP)}px)`,
        }}
      >
        <div
          style={{
            width: 52,
            height: 52,
            borderRadius: "50%",
            overflow: "hidden",
            border: `2px solid ${config.accentColor}`,
            flexShrink: 0,
            transform: `scale(${logoScale})`,
            boxShadow: `0 0 16px ${config.accentColor}50`,
          }}
        >
          <Img
            src={staticFile(config.logoAsset)}
            style={{width: "100%", height: "100%", objectFit: "cover"}}
          />
        </div>
        <div
          style={{
            background: config.badgeBg,
            color: config.badgeText,
            fontFamily: FONT_STACK,
            fontSize: 24,
            fontWeight: 800,
            letterSpacing: "0.06em",
            padding: "7px 16px",
            borderRadius: 8,
          }}
        >
          {badge}
        </div>
      </div>

      {/* Handle — bottom center */}
      <div
        style={{
          position: "absolute",
          bottom: SZ.bottom - 40,
          left: 0,
          right: 0,
          display: "flex",
          justifyContent: "center",
          opacity: opacity * 0.65,
        }}
      >
        <div
          style={{
            fontFamily: FONT_STACK,
            fontSize: 26,
            fontWeight: 500,
            color: config.accentColor,
            letterSpacing: "0.02em",
          }}
        >
          {handle}
        </div>
      </div>
    </AbsoluteFill>
  );
};

// ─── Main Component ───────────────────────────────────────────────────

export const TextReel: React.FC<TextReelProps> = ({
  lines,
  style,
  badge,
  handle = "@guilhermeribeiro.ai",
}) => {
  const config = STYLE_CONFIGS[style];

  const timeline: {start: number; dur: number; idx: number}[] = [];
  let cursor = 0;
  lines.forEach((line, idx) => {
    const dur = framesForLine(line);
    timeline.push({start: cursor, dur, idx});
    cursor += dur + SCENE_TRANSITION;
  });

  return (
    <AbsoluteFill style={{background: config.bg}}>
      <TextReelBg config={config} />
      {timeline.map(({start, dur, idx}) => (
        <Sequence key={idx} from={start} durationInFrames={dur + SCENE_TRANSITION}>
          <LineScene line={lines[idx]} duration={dur + SCENE_TRANSITION} config={config} />
        </Sequence>
      ))}
      <BadgeOverlay badge={badge} handle={handle} config={config} />
    </AbsoluteFill>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
